/**
 * 404 page for unknown routes.
 *
 * Substep 2e — host-aware. On the customer hostname we render a
 * consumer-grade empty state with a CTA back to `/customer/home`
 * (Decision §g). On staff hosts the legacy 404 (with `Back to login`)
 * is preserved so internal users still get the familiar fallback.
 */
import { Link } from 'react-router-dom';
import { IS_CUSTOMER_HOST } from '@/lib/hostnameGuard';
import { Home } from 'lucide-react';

export default function NotFoundPage() {
  if (IS_CUSTOMER_HOST) {
    return <CustomerNotFound />;
  }
  return <StaffNotFound />;
}

function CustomerNotFound() {
  return (
    <div
      className="min-h-[100dvh] bg-[#0A0A0A] text-white flex items-center justify-center text-center px-5 py-10"
      data-testid="not-found-customer"
    >
      <div className="max-w-sm">
        <div className="w-16 h-16 rounded-2xl bg-gold/15 border border-gold/35 grid place-items-center mx-auto mb-5">
          <Home size={26} className="text-gold" />
        </div>
        <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-gold">
          404
        </div>
        <h1 className="font-display text-3xl font-bold mt-3 leading-tight">
          Page not found
        </h1>
        <p className="text-sm text-white/55 mt-2 leading-relaxed">
          The page you&rsquo;re looking for isn&rsquo;t here. Let&rsquo;s
          take you back home.
        </p>
        <Link
          to="/customer/home"
          data-testid="not-found-customer-home"
          className="inline-flex items-center gap-2 mt-6 px-5 py-3 rounded-2xl bg-gold text-black text-sm font-semibold hover:brightness-110 transition-all"
        >
          Back to home
        </Link>
      </div>
    </div>
  );
}

function StaffNotFound() {
  return (
    <div
      className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center text-center px-4"
      data-testid="not-found"
    >
      <div>
        <div className="text-xs uppercase tracking-widest text-gold/80 font-semibold">
          404
        </div>
        <h1 className="font-display text-4xl font-bold mt-4">Page not found</h1>
        <p className="text-white/60 mt-2">
          The page you&rsquo;re looking for doesn&rsquo;t exist.
        </p>
        <Link
          to="/login"
          className="inline-block mt-6 px-5 py-3 rounded-xl border border-gold/30 text-gold hover:bg-gold/10 transition"
        >
          Back to login
        </Link>
      </div>
    </div>
  );
}
